import type { EnumCallStatusApi } from '@ingenico/api-sdk-device-management-private-v2';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { MapperOfStatus } from '@ingenico/react-ui';
import type {
  DatagridQuickFilter,
  DataGridSimpleQuickFilter,
  DefaultQuickFilter,
} from '@ingenico/react-ui/advanced-search';
import { DatagridQuickFilterTypeEnum } from '@ingenico/react-ui/advanced-search';
import { isEmpty } from 'lodash-es';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import type { TFunction } from 'react-i18next';

export function useQuickFiltersSetup(
  t: TFunction,
  mapperOfStatus: MapperOfStatus,
  setSelectedQuickFilterValue: Dispatch<SetStateAction<EnumCallStatusApi[] | undefined>>,
  setOffset: (o: number) => void,
) {
  const getQuickFilters = useCallback(
    () =>
      [
        {
          order: 0,
          field: 'status',
          label: t('field.status', { ns: ALL_LNGS_NAMESPACES.TASKS }),
          options: [],
        },
      ] as DefaultQuickFilter[],
    [t],
  );

  const getLabelQuickFilterLabel = useCallback((name: string) => name, []);
  const quickFilterCall = useCallback(async () => {
    setOffset(0);

    return Promise.resolve(
      Object.entries(mapperOfStatus)
        .filter(([key]) => key !== 'DEFAULT')
        .map(([key, val]) => ({
          label: t(val.labelKey, { ns: ALL_LNGS_NAMESPACES.TASKS }),
          value: key,
        })) ?? [],
    );
  }, [mapperOfStatus, setOffset, t]);

  const checkAllowReset = useCallback((filter?: string) => filter !== 'status', []);

  const overrideDefaultFilterTypes = useCallback((field: string, currentType: DatagridQuickFilterTypeEnum) => {
    if (field === 'status' && currentType === DatagridQuickFilterTypeEnum.AutoComplete) {
      return DatagridQuickFilterTypeEnum.SingleSelect;
    }
    return currentType;
  }, []);

  const handleFilterChange = useCallback(
    (quickFilters: DatagridQuickFilter[]) => {
      if (isEmpty(quickFilters)) {
        setSelectedQuickFilterValue(['' as EnumCallStatusApi]);
      } else {
        const [filter] = quickFilters;
        const { input } = filter as DataGridSimpleQuickFilter;
        const { criteria } = input;
        setSelectedQuickFilterValue(criteria as EnumCallStatusApi[]);
      }
    },
    [setSelectedQuickFilterValue],
  );

  return {
    getQuickFilters,
    getLabelQuickFilterLabel,
    quickFilterCall,
    checkAllowReset,
    overrideDefaultFilterTypes,
    handleFilterChange,
  };
}
